/**
 * ImageDisplay — renders an image asset for a configured duration.
 */

import { useEffect, useRef } from 'react';

export interface ImageDisplayProps {
  /** URL of the image to display */
  src: string;
  /** Duration to display in seconds (informational; timer is handled by usePlayback) */
  duration?: number;
  /** Called when image has loaded */
  onLoad?: () => void;
  /**
   * Called when the image fails to load (broken/expired URL, decode error).
   * Mirrors VideoPlayer's error recovery (#173): a failed image must not sit
   * as a black frame for its full slot duration — the parent advances to the
   * next item instead.
   */
  onError?: () => void;
  /** CSS class name */
  className?: string;
}

export function ImageDisplay({
  src,
  duration,
  onLoad,
  onError,
  className = '',
}: ImageDisplayProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleLoad = () => {
      onLoadRef.current?.();
    };

    // #173: advance on error instead of freezing on a black frame for the
    // item's full slot duration (broken/expired URL, decode failure).
    const handleError = () => {
      console.error('[ImageDisplay] Image failed to load — advancing to next item');
      onErrorRef.current?.();
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      <img
        ref={imgRef}
        src={src}
        alt=""
        className="w-full h-full object-contain"
        draggable={false}
      />
      {duration !== undefined && (
        <div className="absolute bottom-2 right-2 text-xs text-white/40">
          {duration}s
        </div>
      )}
    </div>
  );
}
